// API service with JWT token management
export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'http://localhost';

export const TELEGRAM_SERVICE_BASE_URL =
  (import.meta.env.VITE_TELEGRAM_SERVICE_BASE_URL as string | undefined) || API_BASE_URL;

export interface TelegramSettingsData {
  linked: boolean;
  enabled: boolean;
  username?: string;
  linkedAt?: string;
  linkStatus?: string;
}

export interface UserSettings {
  id: string;
  version: number;
  telegram: TelegramSettingsData;
}

export interface TelegramLinkTokenResponse {
  channel: string;
  linkToken: string;
  expiresAt: string;
  botUrl: string;
}

export interface TelegramServiceStatusResponse {
  configured: boolean;
  username?: string;
  message?: string;
}

const PUBLIC_AUTH_ENDPOINTS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/reset-password',
  '/auth/verify',
]);

class ApiService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('jwt_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('jwt_token', token);
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('jwt_token');
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string = API_BASE_URL
  ): Promise<T> {
    const url = `${baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add Authorization header if token exists
    if (this.token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${this.token}`,
      };
    }

    try {
      const response = await fetch(url, config);

      const isPublicAuthEndpoint = PUBLIC_AUTH_ENDPOINTS.has(endpoint);
      // Handle token expiration for protected routes
      if (response.status === 401 && this.token && !isPublicAuthEndpoint) {
        this.removeToken();
        window.location.href = '/login';
        throw new Error('Token expired. Please login again.');
      }

      if (!response.ok) {
        const rawText = await response.text();
        let message = '';
        if (rawText) {
          try {
            const parsed = JSON.parse(rawText) as { message?: unknown };
            if (parsed && typeof parsed === 'object' && typeof parsed.message === 'string') {
              message = parsed.message;
            } else {
              message = rawText;
            }
          } catch {
            message = rawText;
          }
        }

        throw new Error(message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication endpoints
  async login(email: string, password: string) {
    const response = await this.request<{ token: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    this.setToken(response.token);
    return response;
  }

  async register(userData: any) {
    const response = await this.request<{ id?: string; message?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response;
  }

  async resetPassword(email: string) {
    return this.request<{ message?: string }>('/auth/reset-password', {
      method: 'PATCH',
      body: JSON.stringify({ email }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message?: string }>('/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async logout() {
    this.removeToken();
  }

  // Protected endpoints
  async getUserById(id: string) {
    return this.request<any>(`/api/v1/user/${id}`);
  }

  async updateProfile(id: string, data: any) {
    return this.request<any>(`/api/v1/user/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async searchMusicians(
    filters: Record<string, string | number | Array<string | number>>
  ) {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.append(key, String(item));
        });
        return;
      }

      queryParams.append(key, String(value));
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/user?${queryString}` : '/api/v1/user';
    return this.request<any[]>(endpoint);
  }

  async getConversations() {
    return this.request<any[]>('/messages/conversations');
  }

  async sendMessage(conversationId: string, message: string) {
    return this.request<any>('/messages/send', {
      method: 'POST',
      body: JSON.stringify({ conversationId, message }),
    });
  }

  async getChatsForUser(userId: string, limit?: number, offset?: number) {
    const query = new URLSearchParams({
      participants: userId,
      sort: '-createdAt',
    });
    if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
      query.set('limit', String(limit));
    }
    if (typeof offset === 'number' && Number.isFinite(offset) && offset >= 0) {
      query.set('offset', String(offset));
    }
    return this.request<any[]>(`/api/v1/chat?${query.toString()}`);
  }

  async getMessagesForChat(chatId: string) {
    const query = new URLSearchParams({
      chatId,
      sort: 'createdAt',
    });
    return this.request<any[]>(`/api/v1/message?${query.toString()}`);
  }

  async getUserSettings(userId: string) {
    const query = new URLSearchParams({
      owner: userId,
    });
    return this.request<any[]>(`/api/v1/settings?${query.toString()}`);
  }

  async createUserSettings(data: { telegram: TelegramSettingsData }) {
    return this.request<any>('/api/v1/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserSettings(id: string, data: { telegram: TelegramSettingsData; version: number }) {
    return this.request<any>(`/api/v1/settings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createTelegramLinkToken() {
    return this.request<TelegramLinkTokenResponse>(
      '/telegram/link-token',
      {
        method: 'POST',
      },
      TELEGRAM_SERVICE_BASE_URL
    );
  }

  async getTelegramServiceStatus() {
    return this.request<TelegramServiceStatusResponse>(
      '/telegram/status',
      {
        method: 'GET',
      },
      TELEGRAM_SERVICE_BASE_URL
    );
  }

  async disconnectTelegram() {
    return this.request<{ message?: string }>(
      '/telegram/disconnect',
      {
        method: 'POST',
      },
      TELEGRAM_SERVICE_BASE_URL
    );
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }
}

export const apiService = new ApiService();
